import { useTheme } from '@mui/material';
import { useEffect } from 'react';

import {
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { useGetCohortAttritionByCohortProfileIdQuery } from '@verantos-dev/data-access-cohort-attrition';
import { DataGridMessages } from '@verantos-dev/shared-components';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { CohortAttritionTableProps } from '../CohortAttritionTable';
import { useCohortAttritionColumns } from './useCohortAttritionColumns';
import { useCohortAttritionDataFormatter } from './useCohortAttritionDataFormatter';
import { useCohortAttritionHeaderMessageKeys } from './useCohortAttritionHeaderMessageKeys';
import { useCohortAttritionTableMessageKeys } from './useCohortAttritionTableMessageKeys';
import { useCohortAttritionWorkflowMessageKeys } from './useCohortAttritionWorkflowMessageKeys';

export const useCohortAttritionTable = ({
  projectId,
  cohortId,
}: Omit<CohortAttritionTableProps, 'cohortStatus'>) => {
  const theme = useTheme();
  const latestCohortStatus = useCohortDefinitionStore(selectLatestStatus);

  const attritionHeaderMessages = useCohortAttritionHeaderMessageKeys();
  const attritionTableMessageKeys = useCohortAttritionTableMessageKeys();
  const attritionWorkflowMessagesKey = useCohortAttritionWorkflowMessageKeys();

  const attritionTableMessages: DataGridMessages = {
    error: attritionTableMessageKeys.errorCohortEmptyMessage,
    errorTitle: attritionTableMessageKeys.emptyMessageTitle,
    noResults: attritionTableMessageKeys.defaultEmptyMessage,
    noRows: attritionTableMessageKeys.defaultEmptyMessage,
  };

  const attritionWorkflowMessages = {
    draftTitle: attritionWorkflowMessagesKey.draftTitle,
    draftMessage: attritionWorkflowMessagesKey.draftMessage,
    errorTitle: attritionWorkflowMessagesKey.errorTitle,
    errorMessage: attritionWorkflowMessagesKey.errorMessage,
    processingTitle: attritionWorkflowMessagesKey.processingTitle,
    processingMessage: attritionWorkflowMessagesKey.processingMessage,
  };

  const columns = useCohortAttritionColumns();

  const useFetchCohortAttritionData = () => {
    const { data: unformattedData, ...rest } = useGetCohortAttritionByCohortProfileIdQuery(
      {
        cohortProfileId: cohortId,
        projectId,
      },
      {
        skip: latestCohortStatus !== CohortDefinitionStatus.PROCESSED,
      }
    );
    const formattedData = useCohortAttritionDataFormatter(unformattedData);

    return {
      data: {
        ...unformattedData,
        data: formattedData,
      },
      ...rest,
    };
  };

  const {
    isUninitialized,
    isFetching,
    data: attritionData,
    error: attritionDataError,
    refetch,
  } = useFetchCohortAttritionData();

  useEffect(() => {
    if (latestCohortStatus === CohortDefinitionStatus.PROCESSED) {
      setTimeout(() => {
        refetch();
      }, 1000);
    }
  }, [latestCohortStatus]);

  return {
    theme,
    latestCohortStatus,
    isInitiallyLoading: isUninitialized || isFetching,
    columns,
    attritionHeaderMessages,
    attritionTableMessages,
    attritionWorkflowMessages,
    attritionData,
    attritionDataError,
    useFetchCohortAttritionData,
  };
};
